// Catalog.ID Client — Dashboard Pane
// ============================================================================
// Home workspace overview: active identity, claim counts, notifications, quick actions.

import { createElement, createFieldGroup, createFormRow, createEmptyState } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext, ShellAPI } from '@catalog-shell';
import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import * as publicClaimsApi from '../api/public-claims';
import * as trustApi from '../api/trust';

import { identity } from '../state/identity';
import { session } from '../state/session';
import type { AccountInfo, PublicClaimSummary } from '../api/types';
import {
    CHAT_ROOT_KEY_CLASS,
    REGISTRY_SIGNING_KEY_CLAIM_TYPE as CHAT_ROOT_CLAIM_TYPE,
} from '../crypto/chat-root-publication';
import { PANE_IDS } from '../shell-config';

const TYPE_LABELS: Record<string, string> = {
    natural_person: 'Personal',
    organization: 'Organization',
    delegate: 'Delegate',
    agent: 'Agent',
};

const ROLE_LABELS: Record<string, string> = {
    owner: 'Owner',
    admin: 'Admin',
    treasurer: 'Treasurer',
    viewer: 'Viewer',
};

function formatRoles(roles: string[]): string {
    return roles.map(r => ROLE_LABELS[r] ?? r).join(', ');
}

/**
 * "Your chat identity is not published" — the in-CID half of the chat first-run
 * trigger (chat migration §14 M3; contract in `apps/chat/dev/.m3-gui-notes.md`).
 *
 * WHY IT LIVES ON THE HOME DASHBOARD. Switching the desktop dashboard to the CID
 * tab (`GET /api/open?service=cid`) does NOT reload that tab or carry a path, so
 * a chat banner can bring the user here but cannot point at a pane. The landing
 * view therefore has to say what to do next itself. Adding a cross-module RPC
 * mode to open a pane would mean a new key-grant mode, which needs a relay
 * canonical arm and a `MandatoryVerifiedInboundModes` pin (§17) — far more
 * machinery than a call-to-action on the page the user already lands on.
 *
 * Organizations are skipped: `ClaimTypes.PublishableBy` for
 * `registry_signing_key` excludes them, so nagging one would be dishonest
 * (§15 O16).
 */
function renderChatIdentityCallToAction(
    container: HTMLElement,
    ctx: PaneTypeRenderContext,
    publicClaims: PublicClaimSummary[],
    accountType: string | undefined,
): void {
    if (accountType === 'organization') return;

    const row = publicClaims.find(
        c => c.claimType === CHAT_ROOT_CLAIM_TYPE && c.keyClass === CHAT_ROOT_KEY_CLASS);
    if (row?.status === 'published') return;

    const group = createFieldGroup({ title: 'Chat identity' });
    group.body.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: row?.status === 'pending'
            ? 'Your chat account root is waiting for operator review. Until it is '
                + 'approved, peers cannot verify messages you send.'
            : 'Your chat account root is not published, so nobody can verify messages '
                + 'you send in chat. Publishing it takes one step and is done once.',
    }));

    if (row?.status !== 'pending') {
        const openBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Publish chat root',
        });
        openBtn.addEventListener('click', () => {
            openChatIdentityPane(ctx.shell);
        });
        group.body.appendChild(openBtn);
    }
    container.appendChild(group.element);
}

/**
 * Land on the Chat Identity pane (the ceremony this call to action is about).
 *
 * Switching to the Settings workspace is not enough on its own: Settings holds
 * five panes across two cells, and the one that runs the ceremony is a tab
 * behind Settings and Devices, so the user arrives with nothing on screen that
 * mentions chat. Activate the pane if the workspace already holds it (the
 * normal case — it is in the configured layout), otherwise open it.
 */
export function openChatIdentityPane(shell: ShellAPI): void {
    shell.setActiveWorkspace('settings');
    const surface = shell.getRootSurface();
    if (!surface) return;
    const existing = surface.getPanes().find(p => p.typeId === PANE_IDS.chatRoot);
    if (existing) {
        surface.setActivePane(existing.instanceId);
        return;
    }
    surface.openPane({ typeId: PANE_IDS.chatRoot, title: 'Chat Identity' });
}

export function createDashboardPane(): PaneType {
    return {
        typeId: PANE_IDS.dashboard,
        kind: 'leaf',
        defaultTitle: 'Dashboard',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderDashboard(container, ctx);
            return container;
        },
    };
}

async function renderDashboard(container: HTMLElement, ctx: PaneTypeRenderContext): Promise<void> {
    const active = identity.getActive();

    // Fetch account info from server (has creationTime, org/principal links)
    let accountInfo: AccountInfo | null = null;
    try {
        accountInfo = await accountApi.getAccountInfo();
    } catch {
        // Fall back to session state
    }

    // Identity section
    const idGroup = createFieldGroup({ title: 'Active Identity' });

    if (active) {
        const cidRow = createFormRow({ label: 'Catalog.ID' });
        cidRow.content.appendChild(createElement('span', {
            className: 'cid-profile-cid',
            textContent: active.cid,
        }));
        idGroup.body.appendChild(cidRow.element);

        const typeRow = createFormRow({ label: 'Type' });
        typeRow.content.appendChild(createElement('span', {
            textContent: TYPE_LABELS[accountInfo?.accountType ?? active.accountType] ?? active.accountType,
        }));
        idGroup.body.appendChild(typeRow.element);

        const statusRow = createFormRow({ label: 'Status' });
        statusRow.content.appendChild(createElement('span', {
            textContent: accountInfo?.status ?? session.getInfo()?.status ?? '',
        }));
        idGroup.body.appendChild(statusRow.element);

        if (accountInfo?.creationTime) {
            const dateRow = createFormRow({ label: 'Created' });
            dateRow.content.appendChild(createElement('span', {
                textContent: new Date(accountInfo.creationTime).toLocaleDateString(),
            }));
            idGroup.body.appendChild(dateRow.element);
        }

        const orgCid = accountInfo?.organizationCid ?? active.organizationCid;
        if (orgCid) {
            const orgRow = createFormRow({ label: 'Organization' });
            const orgLink = createElement('a', {
                className: 'cid-profile-link',
                textContent: orgCid,
            });
            orgLink.setAttribute('href', '#');
            orgLink.addEventListener('click', (e) => {
                e.preventDefault();
                ctx.surface?.openPane({
                    typeId: PANE_IDS.publicProfile,
                    title: orgCid,
                    context: { cid: orgCid },
                }, { originPaneId: ctx.instance.instanceId });
            });
            orgRow.content.appendChild(orgLink);
            idGroup.body.appendChild(orgRow.element);
        }

        const roles = accountInfo?.roles ?? (active.roles ?? []);
        if (roles && roles.length > 0) {
            const rolesRow = createFormRow({ label: 'Roles' });
            rolesRow.content.appendChild(createElement('span', {
                textContent: formatRoles(roles),
            }));
            idGroup.body.appendChild(rolesRow.element);
        }

        if (accountInfo?.isOperator || session.isOperator()) {
            const opRow = createFormRow({ label: 'Role' });
            opRow.content.appendChild(createElement('span', {
                className: 'cid-dashboard-operator-badge',
                textContent: 'Operator',
            }));
            idGroup.body.appendChild(opRow.element);
        }
    } else {
        idGroup.body.appendChild(createEmptyState({ message: 'Not logged in' }));
    }
    container.appendChild(idGroup.element);

    // Claims summary
    const statsGroup = createFieldGroup({ title: 'Claims Summary' });
    const statsLoading = createElement('span', { className: 'cid-wizard-status', textContent: 'Loading...' });
    statsGroup.body.appendChild(statsLoading);
    container.appendChild(statsGroup.element);

    try {
        const claims = await claimsApi.listClaims();
        // Publication state is supplementary (same rule as claims-list-pane):
        // a `partial` delegate — activated, org keys not yet wrapped by an
        // owner — is refused on /api/publicclaims by the partial-account gate,
        // and that must not blank the whole stats block as "Failed to load
        // claims" (seen in the M13 live test, 2026-09-04).
        const publicClaims = await publicClaimsApi.listPublicClaims().catch((): PublicClaimSummary[] => []);
        statsGroup.body.removeChild(statsLoading);

        const totalRow = createFormRow({ label: 'Total Claims' });
        totalRow.content.appendChild(createElement('span', { textContent: String(claims.length) }));
        statsGroup.body.appendChild(totalRow.element);

        const validated = claims.filter(c =>
            c.validationStatus === 'validated' && !c.revokedAt
        ).length;
        const valRow = createFormRow({ label: 'Validated' });
        valRow.content.appendChild(createElement('span', { textContent: String(validated) }));
        statsGroup.body.appendChild(valRow.element);

        const published = publicClaims.filter(c => c.status === 'published').length;
        const pubRow = createFormRow({ label: 'Published' });
        pubRow.content.appendChild(createElement('span', { textContent: String(published) }));
        statsGroup.body.appendChild(pubRow.element);

        const pending = publicClaims.filter(c => c.status === 'pending').length;
        if (pending > 0) {
            const pendRow = createFormRow({ label: 'Pending Publication' });
            pendRow.content.appendChild(createElement('span', { textContent: String(pending) }));
            statsGroup.body.appendChild(pendRow.element);
        }

        renderChatIdentityCallToAction(container, ctx, publicClaims,
            accountInfo?.accountType ?? active?.accountType);
    } catch {
        statsGroup.body.removeChild(statsLoading);
        statsGroup.body.appendChild(createElement('span', {
            className: 'cid-pane-error cid-pane-error--visible',
            textContent: 'Failed to load claims',
        }));
    }

    // Trust score (personal identities only)
    if (identity.isPersonal() && active) {
        const trustGroup = createFieldGroup({ title: 'Trust Score' });
        const trustLoading = createElement('span', { className: 'cid-wizard-status', textContent: 'Loading...' });
        trustGroup.body.appendChild(trustLoading);
        container.appendChild(trustGroup.element);

        void trustApi.getTrustScore(active.cid).then(score => {
            trustGroup.body.removeChild(trustLoading);

            const scoreRow = createFormRow({ label: 'Global Score' });
            scoreRow.content.appendChild(createElement('span', {
                className: 'cid-trust-score-value',
                textContent: score.score.toFixed(4),
            }));
            trustGroup.body.appendChild(scoreRow.element);

            const tierRow = createFormRow({ label: 'Tier' });
            const tierBadge = createElement('span', {
                className: 'cid-trust-tier-badge',
                textContent: `Tier ${score.tier}`,
            });
            tierBadge.setAttribute('data-tier', String(score.tier));
            tierRow.content.appendChild(tierBadge);
            trustGroup.body.appendChild(tierRow.element);

            const ratingsRow = createFormRow({ label: 'Ratings' });
            ratingsRow.content.appendChild(createElement('span', {
                textContent: `${score.incomingCount} received, ${score.outgoingCount} given`,
            }));
            trustGroup.body.appendChild(ratingsRow.element);
        }).catch(() => {
            trustGroup.body.removeChild(trustLoading);
            trustGroup.body.appendChild(createElement('span', {
                className: 'cid-wizard-status',
                textContent: 'Trust data unavailable',
            }));
        });
    }

    // "Quick Actions" used to sit here — New Claim, Look Up Profile, Register
    // Organization. None of them was about the dashboard's own subject: they
    // are module-level verbs, so they now live in the top bar and the gear menu
    // (`cidModuleActions` in register-pane-types.ts; shell USAGE, "Where a
    // button belongs"). The chat call to action above stays, because it belongs
    // to the field group that explains it.
}
